// Route: /audio-recaps/share/$token — публичная read-only страница для
// прослушивания расшаренного аудио-резюме. Auth НЕ требуется.
//
// ⚠ «Auth не требуется» держится ДВУМЯ вещами, и обе обязательны:
//   1. серверные роуты `/api/audio-recaps/shared/:token*` публичны;
//   2. путь занесён в `PUBLIC_EXCEPTION_PREFIXES` (`lib/protected-routes.ts`).
// Без второго страница лежит под закрытым префиксом `/audio-recaps`, и гость
// получал модалку входа вместо записи — то есть кнопка «Поделиться» выдавала
// ссылку, по которой никто, кроме автора, ничего не слышал (инцидент 2026-08-09).
//
// Что показываем постороннему: плеер, транскрипт репликами и скачивание файла.
// Чего НЕ показываем: ссылок на материалы автора — сервер не отдаёт
// `sourceChunkIds`, и состав чужой библиотеки по публичной ссылке не утекает.
//
// PHASE-4-AUDIO-SUMMARY.md §10.3.
// Стриминг идёт через raw Fastify route `/api/audio-recaps/shared/:token/stream`.
//
// Тактильный редизайн «Картотека»: панель e2 по центру, тактильная клавиша
// play, утопленный скраб-трек, единое состояние ошибки.

import { Button, cn, ElevatedPanel, PANEL_PAD, Slider } from "@studyhub/ui";
import {
	IconArrowRight,
	IconDownload,
	IconExclamationCircle,
	IconHeadphones,
	IconLoader2,
	IconPlayerPauseFilled,
	IconPlayerPlayFilled,
	IconSparkles,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiUrl } from "@/lib/api-base";
import { startPlayback } from "@/lib/play-media";

interface SharedRecapSegment {
	index: number;
	text: string;
	role: string | null;
	startSec: number | null;
}

interface SharedRecapMeta {
	title: string;
	format: string;
	durationSec: number;
	language: string;
	segments: SharedRecapSegment[];
}

/** Публичные метаданные записи. Без аккаунта, без ссылок на материалы автора. */
function useSharedRecap(token: string) {
	return useQuery<SharedRecapMeta | null>({
		queryKey: ["audio-recaps", "shared", token],
		retry: false,
		staleTime: 5 * 60_000,
		queryFn: async () => {
			const res = await fetch(apiUrl(`/api/audio-recaps/shared/${token}`));
			if (!res.ok) return null;
			const body = (await res.json()) as { ok: boolean; data?: SharedRecapMeta };
			return body.ok && body.data ? body.data : null;
		},
	});
}

// Public share-link page. Route поднимается в `routeTree.gen.ts` плагином;
// пока тип не сгенерирован — any-каст.
// biome-ignore lint/suspicious/noExplicitAny: TanStack Router type-gen для нового nested route
export const Route = (createFileRoute as any)("/audio-recaps/share/$token")({
	component: SharedAudioRecapPage,
});

function SharedAudioRecapPage(): React.ReactElement {
	const { t } = useTranslation("audio");
	const { token } = Route.useParams() as { token: string };
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [position, setPosition] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const meta = useSharedRecap(token);
	const streamUrl = apiUrl(`/api/audio-recaps/shared/${token}/stream`);

	useEffect(() => {
		const audio = new Audio(apiUrl(`/api/audio-recaps/shared/${token}/stream`));
		audio.preload = "metadata";
		audio.addEventListener("loadedmetadata", () => {
			setDuration(audio.duration || 0);
		});
		audio.addEventListener("timeupdate", () => {
			setPosition(audio.currentTime);
		});
		audio.addEventListener("ended", () => setIsPlaying(false));
		audio.addEventListener("error", () => {
			setError(t("share.publicPage.error"));
			setIsPlaying(false);
		});
		audioRef.current = audio;
		return () => {
			audio.pause();
			audio.removeAttribute("src");
			audio.load();
		};
	}, [token, t]);

	function toggle(): void {
		const audio = audioRef.current;
		if (!audio) return;
		if (audio.paused) {
			void startPlayback(audio).then(setIsPlaying);
		} else {
			audio.pause();
			setIsPlaying(false);
		}
	}

	function seek(sec: number): void {
		const audio = audioRef.current;
		if (!audio) return;
		audio.currentTime = sec;
		setPosition(sec);
	}

	// Метаданные ещё грузятся: duration неизвестна и нет ошибки. На медленном
	// mobile-соединении показываем спиннер вместо «готового» неиграющего плеера.
	const isLoadingMeta = duration === 0 && !error;

	return (
		<div className="flex min-h-dvh flex-col bg-background">
			<header className="flex items-center justify-between border-b border-border px-4 py-3">
				<div className="flex items-center gap-2 text-foreground">
					<span className="inline-flex size-7 items-center justify-center rounded-[7px] bg-[var(--c-cards-bg)] text-[var(--c-cards-fg)] [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]">
						<IconSparkles size={15} />
					</span>
					<span className="font-semibold">StudyHub</span>
				</div>
				<Button asChild variant="outline" size="sm" className="pointer-coarse:h-11">
					<Link to="/">
						{t("share.publicPage.createOwn")} <IconArrowRight size={14} />
					</Link>
				</Button>
			</header>

			<main className="flex flex-1 items-center justify-center px-4 py-8">
				<ElevatedPanel className={cn(PANEL_PAD, "w-full max-w-xl")}>
					<div className="mb-6 flex items-center gap-3">
						<div className="flex size-12 items-center justify-center rounded-[var(--radius-tile)] bg-[var(--c-cards-bg)] text-[var(--c-cards-fg)] [box-shadow:inset_0_1px_0_rgb(255_255_255/0.45)]">
							<IconHeadphones size={24} />
						</div>
						<div>
							<h1 className="text-lg font-semibold text-foreground">
								{t("share.publicPage.heading")}
							</h1>
							<p className="text-sm text-muted-foreground">{t("share.publicPage.subheading")}</p>
						</div>
					</div>

					{error ? (
						<div className="flex flex-col items-center gap-2 rounded-[var(--radius-card)] border border-destructive/30 bg-card px-6 py-8 text-center shadow-e1">
							<div className="flex size-11 items-center justify-center rounded-[var(--radius-tile)] bg-destructive/12 text-destructive">
								<IconExclamationCircle size={22} />
							</div>
							<p className="text-sm text-muted-foreground">{error}</p>
						</div>
					) : (
						<>
							<div className="mb-5">
								<Slider
									value={[position]}
									max={duration || 1}
									step={0.1}
									disabled={isLoadingMeta}
									onValueChange={(v) => seek(v[0] ?? 0)}
									aria-label={t("share.publicPage.position")}
								/>
								<div className="num mt-1.5 flex justify-between text-2xs tabular-nums text-muted-foreground">
									<span>{formatTime(position)}</span>
									<span>{formatTime(duration)}</span>
								</div>
							</div>

							<div className="flex flex-col items-center justify-center gap-2">
								<Button
									onClick={toggle}
									size="lg"
									disabled={isLoadingMeta}
									className="size-14 rounded-full p-0"
									aria-label={isPlaying ? t("share.publicPage.pause") : t("share.publicPage.play")}
								>
									{isLoadingMeta ? (
										<IconLoader2 size={20} className="animate-spin" />
									) : isPlaying ? (
										<IconPlayerPauseFilled size={20} />
									) : (
										<IconPlayerPlayFilled size={20} />
									)}
								</Button>
								{isLoadingMeta && (
									<p className="text-xs text-muted-foreground">{t("share.publicPage.loading")}</p>
								)}
								{/* Скачивание — обычная ссылка на публичный стрим: сервер
								    уже отдаёт файл по токену, отдельного эндпоинта не нужно. */}
								<Button asChild variant="ghost" size="sm" className="pointer-coarse:h-11">
									<a href={streamUrl} download>
										<IconDownload size={14} />
										{t("share.publicPage.download")}
									</a>
								</Button>
							</div>

							{/* Транскрипт репликами — без чипов источников: по публичной
							    ссылке слушает посторонний, состав библиотеки автора его
							    не касается (сервер `sourceChunkIds` и не отдаёт). */}
							{meta.data && meta.data.segments.length > 0 ? (
								<section className="mt-6 border-t border-border pt-4">
									<h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
										{t("transcript.title")}
									</h2>
									<ol className="space-y-2.5">
										{meta.data.segments.map((seg) => (
											<li key={seg.index} className="flex min-w-0 gap-2.5">
												{seg.startSec !== null ? (
													<button
														type="button"
														onClick={() => seek(seg.startSec ?? 0)}
														className="shrink-0 cursor-pointer pt-0.5 text-2xs tabular-nums text-muted-foreground hover:text-primary"
														aria-label={t("transcript.seekToSegment", {
															ts: formatTime(seg.startSec ?? 0),
														})}
													>
														{formatTime(seg.startSec ?? 0)}
													</button>
												) : null}
												<p className="min-w-0 flex-1 text-sm leading-relaxed text-foreground">
													{seg.text}
												</p>
											</li>
										))}
									</ol>
								</section>
							) : null}
						</>
					)}

					<p className="mt-6 border-t border-border pt-4 text-center text-xs text-muted-foreground">
						{t("share.publicPage.footerHint")}{" "}
						<Link to="/" className="text-primary underline-offset-4 hover:underline">
							{t("share.publicPage.footerCta")}
						</Link>
					</p>
				</ElevatedPanel>
			</main>
		</div>
	);
}

function formatTime(sec: number): string {
	if (!Number.isFinite(sec) || sec < 0) return "0:00";
	const m = Math.floor(sec / 60);
	const s = Math.floor(sec % 60);
	return `${m}:${s.toString().padStart(2, "0")}`;
}
